import posthog from 'posthog-js';
import { getKeycloak } from './auth/keycloak-instance';
import { candidateEnv } from './config/env';

/**
 * PostHog — product analytics + session replay no App Candidato (EU-331), no
 * mesmo modelo adotado para a Intercom (EU-320): config-first, boot único
 * depois que o Keycloak resolve, identidade lida do token KC, no-op quando
 * desabilitado.
 *
 * A Project API Key (`phc_...`, pública, baked in at build time) vem de
 * `VITE_POSTHOG_KEY`, setada por ambiente no workflow de deploy; o host
 * (`VITE_POSTHOG_HOST`, default US Cloud) idem. Vazio/ausente → boot vira
 * no-op (analytics + replay desabilitados). @see src/intercom.ts @see src/gtm.ts
 *
 * Projeto único stg+prd: o plano free do PostHog só permite 1 projeto, então os
 * dois ambientes compartilham a mesma key e cada evento carimba a
 * super-propriedade `env` (`dev`/`stg`/`prd`, via `VITE_APP_ENV`) — réplica da
 * convenção de `env` do pipeline Mixpanel (APP_ENV). Filtra-se o ambiente
 * dentro do PostHog.
 *
 * Cobre as DUAS frentes da transição (coexistência v1↔v2 e V2) por construção:
 * é o mesmo build do SPA; bootar no `KeycloakGate` ativa a captura em ambas.
 */

const DEFAULT_POSTHOG_HOST = 'https://us.i.posthog.com';

type PosthogIdentity = { distinctId: string; name?: string; email?: string };

/**
 * Lê a identidade do candidato do token Keycloak. O `distinct_id` do PostHog usa
 * o `sub` (subject do Keycloak) — o identificador canônico de participante usado
 * no resto do SPA (mesmo `participantId` do Mixpanel/Intercom). `name`/`email`
 * vêm dos scopes padrão do KC. Devolve `null` quando deslogado ou antes do
 * Keycloak resolver → captura anônima (sem identify).
 */
function resolvePosthogIdentity(): PosthogIdentity | null {
  try {
    const kc = getKeycloak();
    const t = kc.tokenParsed as Record<string, unknown> | undefined;
    if (!kc.authenticated || !t) return null;
    const distinctId = typeof t.sub === 'string' && t.sub ? t.sub : '';
    if (!distinctId) return null;
    const name = typeof t.name === 'string' ? t.name : undefined;
    const email = typeof t.email === 'string' ? t.email : undefined;
    return {
      distinctId,
      ...(name ? { name } : {}),
      ...(email ? { email } : {}),
    };
  } catch {
    // Antes do Keycloak inicializar — trata como anônimo.
    return null;
  }
}

/**
 * Inicializa o PostHog uma única vez, com a identidade resolvida do estado de
 * auth atual. Deve ser chamado depois que o Keycloak resolve (ver `KeycloakGate`).
 * No-op quando `VITE_POSTHOG_KEY` não está setado.
 *
 * `defaults: '2025-05-24'` aplica os defaults modernos recomendados (incl.
 * `$pageview` por mudança de history — correto p/ SPA TanStack Router — e
 * `person_profiles: 'identified_only'`, custo-eficiente). O session replay é
 * ligado no projeto (server-side opt-in); o SDK grava conforme essa config.
 * Como o login sempre faz full-reload (`keycloak.login()` redireciona), uma
 * init por carregamento de página já reflete o estado correto.
 */
export function bootPosthog(): void {
  const apiKey = candidateEnv.posthogKey?.trim();
  if (!apiKey) return;

  posthog.init(apiKey, {
    api_host: candidateEnv.posthogHost?.trim() || DEFAULT_POSTHOG_HOST,
    defaults: '2025-05-24',
    loaded: (ph) => {
      // `env` (dev/stg/prd) em todo evento — distingue ambientes no projeto único.
      const env = candidateEnv.appEnv?.trim();
      if (env) ph.register({ env });

      const identity = resolvePosthogIdentity();
      if (identity) {
        ph.identify(identity.distinctId, {
          ...(identity.name ? { name: identity.name } : {}),
          ...(identity.email ? { email: identity.email } : {}),
        });
      }
    },
  });
}

/**
 * Encerra a sessão do PostHog no logout. `reset()` limpa o `distinct_id`/
 * identidade e a super-propriedade `env` antes do redirect de logout do
 * Keycloak, evitando que um próximo usuário no mesmo navegador herde a
 * identidade/sessão anterior (réplica do shutdown da Intercom). No-op quando o
 * PostHog não está habilitado (`VITE_POSTHOG_KEY` ausente).
 */
export function shutdownPosthog(): void {
  if (!candidateEnv.posthogKey?.trim()) return;
  posthog.reset();
}
